import { useCallback, useState } from 'react';
import Papa from 'papaparse';
import { useClient } from '../../../context/client';
import { alertsManager } from '../../../utils';

const createAndRule =
	(...rules) =>
	(value) =>
		rules.every((rule) => rule(value));
const createOrRule =
	(...rules) =>
	(value) =>
		rules.some((rule) => rule(value));
const withTransformation = (transformation, rule) => (value) =>
	rule(transformation(value));

const isNull = (value) => value === null;
const isInteger = (value) => Number.isInteger(value);
const isString = (value) => typeof value === 'string';
const isNotEmpty = (value) => value.length > 0;
const isArray = (value) => value instanceof Array;
const isValue = (valueCheck) => (value) => value === valueCheck;
const isNonEmptyString = createAndRule(isString, isNotEmpty);
const isNonEmptyStringOrInt = createOrRule(
	isInteger,
	createAndRule(isString, isNotEmpty),
);
const areValuesValid = (rule) => (values) => createAndRule(...values.map(rule));
const stringAsArray = (separator) => (value) => value.split(separator);

const IMPORT_RULES = [
	{
		selector: 'id',
		required: false,
		rule: isInteger,
		message: 'must be an integer',
	},
	{
		selector: 'available',
		required: false,
		rule: createOrRule(isValue(true), isValue(false)),
		message: 'must be TRUE or FALSE',
	},
	{
		selector: 'sku',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'vendor',
		required: false,
		rule: isNonEmptyString,
		message: 'must be a non-empty string',
	},
	{
		selector: 'comment',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'year',
		required: true,
		rule: isInteger,
		message: 'must be a non-empty string',
	},
	{
		selector: 'make',
		required: true,
		rule: isNonEmptyString,
		message: 'must be a non-empty string',
	},
	{
		selector: 'model',
		required: true,
		rule: isNonEmptyStringOrInt,
		message: 'must be a non-empty string',
	},
	{
		selector: 'sub_model',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'part_number',
		required: false,
		rule: createOrRule(isString, isInteger),
		message: 'must be a valid string',
	},
	{
		selector: 'row',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'seat_description',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'legacy_id',
		required: false,
		rule: isInteger,
		message: 'must be a valid integer',
	},
	{
		selector: 'diagram',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_1_title',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_1_description',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_1_image',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_2_title',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_2_description',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'tip_2_image',
		required: false,
		rule: isString,
		message: 'must be a valid string',
	},
	{
		selector: 'evox_vif',
		required: false,
		rule: isInteger,
		message: 'must be a valid integer',
	},
	{
		selector: 'sort_by',
		required: false,
		rule: isInteger,
		message: 'must be a valid integer',
	},
	{
		selector: 'delete',
		required: false,
		rule: createOrRule(
			isNull,
			createAndRule(
				isString,
				withTransformation(
					(value) => value.toLowerCase() === 'y',
					createOrRule(isValue(true), isValue(false)),
				),
			),
		),
		message: 'must be Y or N',
	},
	{
		selector: 'add_on_skus',
		required: false,
		rule: createAndRule(
			isString,
			withTransformation(
				stringAsArray(','),
				createAndRule(
					isArray,
					areValuesValid(createAndRule(isString, isNotEmpty)),
				),
			),
		),
		message: 'must be an array of valid SKUs',
	},
	{
		selector: function selectQuestions(columnName) {
			return columnName.toLowerCase().startsWith('question_');
		},
		required: false,
		rule: createAndRule(
			isString,
			withTransformation(
				stringAsArray('\n'),
				(values) => values.length >= 2,
			),
		),
		message: 'must be null or a non-empty string with at least 2 lines',
	},
];

function validateCsvRow(row) {
	return IMPORT_RULES.flatMap(({ selector, required, rule, message }) => {
		if (typeof selector === 'string') {
			const cellValue = row[selector];

			if (cellValue === undefined || cellValue === null)
				return required ? `${selector} is a required field` : [];

			if (!rule(cellValue)) return `${selector} ${message}`;

			return [];
		}

		return Object.keys(row)
			.filter((column) => selector(column))
			.flatMap((column) => {
				const cellValue = row[column];

				if (cellValue === undefined || cellValue === null)
					return required ? `${column} is a required field` : [];

				return !rule(cellValue) ? `${column} ${message}` : [];
			});
	});
}

export function validateImportFile(csvData) {
	return new Promise((resolve) => {
		const rows = [];

		Papa.parse(csvData.trim(), {
			worker: true,
			header: true,
			dynamicTyping: true,
			step: (row) => {
				const validationErrors = validateCsvRow(row.data);

				rows.push({
					...row.data,
					errors: validationErrors.join('. '),
				});
			},
			complete: () => resolve(rows),
		});
	});
}

export function readFileAsync(file) {
	return new Promise((resolve) => {
		const fileReader = new FileReader();
		fileReader.onload = async (event) => {
			resolve(event.target.result);
		};
		fileReader.readAsText(file);
	});
}

// TODO: Add file content validation with papa parse
export function useImportFitment(onImportComplete) {
	const [importing, setImporting] = useState(
		/** @type {string | false} */ false,
	);
	const client = useClient();

	const dispatchFileImport = useCallback(
		async function dispatch(fileContents) {
			setImporting('Uploading Fitment Data...0%');

			try {
				await client.importFitment(fileContents, setImporting);
				alertsManager.add({
					header: 'Fitment Import Success',
					type: 'success',
					autoDismiss: true,
				});
				onImportComplete();
			} catch (e) {
				alertsManager.add({
					header: 'Fitment Import Error',
					messages: [
						{
							text: 'There was an error importing the file. Please check for errors and try again',
						},
					],
					type: 'error',
					autoDismiss: true,
				});
			} finally {
				setImporting(false);
			}
		},
		[onImportComplete],
	);

	return [importing, dispatchFileImport];
}
